import { Alert, Anchor, Stack, Text } from "@mantine/core";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { QueueControls } from "./QueueControls";
import type { QueueStateResponse } from "./api";
import { requestErrorText } from "./requestErrorText";

// Разводит четыре состояния очереди на странице /queue: рабочая
// панель управления, раннер не зарегистрирован (`state.queue === null`
// из `GET /queue` — штатное состояние чистой установки, не ошибка),
// загрузка (данных ещё нет) и ошибка запроса. Закэшированные данные
// приоритетнее транзиентной ошибки текущего тика: polling каждые 2
// секунды регулярно ловит блип (один неудачный тик на фоне рабочей
// очереди) — панель не должна дёргаться в красный `Alert` на каждый
// такой блип, пока есть данные, которые можно показать. `error`
// смотрим только когда данных ещё не было вовсе (`state === undefined`).
// Явные ранние return'ы вместо вложенных тернариев (см.
// `QuotaIndicator`).
export function QueueSection({
  state,
  error,
  hasActiveTask,
}: {
  readonly state: QueueStateResponse | undefined;
  readonly error: Error | null;
  readonly hasActiveTask: boolean;
}): ReactElement {
  const { t } = useTranslation();

  if (state !== undefined) {
    if (state.queue === null) {
      return (
        <Alert color="yellow" title={t("queue.unprovisionedTitle")}>
          <Stack gap="xs">
            <Text size="sm">{t("queue.unprovisionedBody")}</Text>
            <Anchor component={Link} to="/settings/runners">
              {t("queue.registerRunnerLink")}
            </Anchor>
            <Text size="sm">{t("queue.unprovisionedAdmin")}</Text>
          </Stack>
        </Alert>
      );
    }
    return <QueueControls queue={state.queue} hasActiveTask={hasActiveTask} />;
  }

  if (error === null) {
    return <Text c="dimmed">{t("queue.stateLoading")}</Text>;
  }

  return (
    <Alert color="red" title={t("queue.stateErrorTitle")}>
      {requestErrorText(error, t)}
    </Alert>
  );
}
